// context/ProjectContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FilterCriteria, PaginatedResult, Project, ProjectStatus, SortCriteria } from '../types';
import { api } from '../services/api.ts';
import { useAuth } from './AuthContext.tsx';

interface ProjectContextType {
  projects: Project[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  fetchProjects: (page?: number, size?: number, filters?: FilterCriteria, sort?: SortCriteria) => Promise<void>;
  addProject: (project: Project) => void;
  updateProjectStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
  filters: FilterCriteria;
  updateFilters: (newFilters: Partial<FilterCriteria>) => void;
  sort: SortCriteria;
  updateSort: (field: keyof Project) => void;
  setCurrentPage: (page: number) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);
export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Existing state
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Consolidate filter/sort state here
  const [filters, setFilters] = useState<FilterCriteria>({});
  const [sort, setSort] = useState<SortCriteria>({
    field: 'createdAt',
    direction: 'descending',
  });

  // Update filters method
  const updateFilters = useCallback((newFilters: Partial<FilterCriteria>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, []);

  // Update sort method
  const updateSort = useCallback((field: keyof Project) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  }, []);
  const fetchProjects = useCallback(async (
    page: number = currentPage,
    size: number = pageSize,
  ) => {
    setIsLoading(true);
    setError(null);
    
    // Build query parameters
    const params: Record<string, string> = {
      pageNumber: page.toString(),
      pageSize: size.toString(),
      sortField: sort.field.toString(),
      sortDirection: sort.direction.charAt(0).toUpperCase() + sort.direction.slice(1) // Convert to 'Ascending' or 'Descending'
    };
    
    // Add filter parameters if they exist and have values
    if (filters.client) params.client = filters.client;
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.searchTerm) params.searchTerm = filters.searchTerm;
    if (filters.projectId) params.projectId = filters.projectId;
    
    try {
      const response = await api.get<PaginatedResult<Project>>('projects', params);
      
      setProjects(response.items);
      setTotalCount(response.totalCount);
      setCurrentPage(response.pageNumber);
      setPageSize(response.pageSize);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching projects');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, sort]);

  // Effect to fetch when dependencies change
  useEffect(() => {
    if(user){
      fetchProjects();
    }
  }, [fetchProjects, user]);

  // Add project method
  const addProject = (project: Project) => {
    setProjects(prevProjects => [...prevProjects, project]);
  };

  // Update project status method
  const updateProjectStatus = async (projectId: string, newStatus: ProjectStatus) => {
    try {
      await api.patch(`projects/${projectId}`, { status: newStatus });
      setProjects(prevProjects =>
        prevProjects.map(project =>
          project.id === projectId ? { ...project, status: newStatus } : project
        )
      );
    }
    catch(Error) {
      console.error('An error occurred while updating project status:', Error);
    }
  };

  return (
    <ProjectContext.Provider 
      value={{ 
        projects, 
        totalCount, 
        currentPage, 
        pageSize, 
        totalPages,
        isLoading, 
        error,
        filters,
        sort,
        updateFilters,
        updateSort,
        fetchProjects, 
        setCurrentPage, // Expose for pagination
        // ...other methods
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};